import { redirect } from '@tanstack/react-router';
import { createIsomorphicFn } from '@tanstack/react-start';

import { HTTPClient } from './http-client';
import { safeInvoke } from './safe-invoke';
import type { SessionData } from './session';

import { REQUEST_HEADERS } from '@/constants';
import { serverEnv } from '@/env';
import { refreshServerFn } from '@/server/auth/refresh';
import { getUserSession } from '@/server/user';

let clientApiClientInstance: HTTPClient | null = null;
let proxylessApiClientInstance: HTTPClient | null = null;

/**
 * @description *Server Only* Options for the getServerApiClient function
 * @default { authenticated: true, includeTenantHeader: true }
 */
export type ServerAPIClientOptions = {
  authenticated?: boolean; // @default true
  includeTenantHeader?: boolean;
  session?: SessionData;
};

const DEFAULT_OPTIONS: ServerAPIClientOptions = {
  authenticated: true,
  includeTenantHeader: true,
  session: undefined,
};

export const apiClient = createIsomorphicFn()
  .server(async (options: ServerAPIClientOptions = DEFAULT_OPTIONS): Promise<HTTPClient> => {
    const { authenticated, includeTenantHeader } = { ...DEFAULT_OPTIONS, ...options };

    const baseUrl = serverEnv.TESSERA_BACKEND_URL;

    if (!authenticated) return new HTTPClient({ baseUrl });

    const { authentication, tenantId } = options.session ?? (await getUserSession()) ?? {};

    if (!authentication?.accessToken) console.error('[API CLIENT] No access token found');
    if (includeTenantHeader && !tenantId) console.error('[API CLIENT] No tenant ID found');

    return new HTTPClient({
      baseUrl,
      onUnauthorized: () => {
        console.info('[API CLIENT] Unauthorized. Redirecting to login.');
        throw redirect({ to: '/login' });
      },
      onRequestHeaders: () => {
        return {
          Authorization: `Bearer ${authentication?.accessToken}`,
          'X-Refresh-Token': authentication?.refreshToken,
          ...(includeTenantHeader && tenantId ? { [REQUEST_HEADERS.TENANT_ID]: tenantId } : {}),
        };
      },
    });
  })
  .client(() => {
    if (!clientApiClientInstance) {
      clientApiClientInstance = new HTTPClient({
        baseUrl: '/api',
        onUnauthorized: async () => {
          console.info('[API CLIENT] Refreshing token.');
          const redirectTo = typeof window !== 'undefined' ? window?.location?.href : undefined;
          const response = await safeInvoke(refreshServerFn({ data: { redirectTo } }));
          if (response.error) {
            console.error('[API CLIENT] Error refreshing token:', response.error);
            return false;
          }
          return true;
        },
      });
    }

    return clientApiClientInstance;
  });

type APIClientOptions = {
  server?: ServerAPIClientOptions;
};

export async function getApiClient(options?: APIClientOptions) {
  return apiClient(options?.server);
}

export const proxylessApiClient = createIsomorphicFn()
  .server(() => {
    return new HTTPClient({});
  })
  .client(() => {
    if (!proxylessApiClientInstance) {
      proxylessApiClientInstance = new HTTPClient({});
    }
    return proxylessApiClientInstance;
  });
